import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import { formatNumber, getCurrency } from 'modules/I18N';
import { Store } from 'components/common/types/Store.types';
import { InputAlign } from 'lib/BaseInput';
import InputNumber from 'lib/InputNumber';
import { getChipColor } from 'utils/getChipColor';
import Chip, { Color, Size } from 'lib/Chip';
import { isDefaultValueError, isRangeValueError } from 'components/pages/PCM/Products/utils/validateRangeValues';
import PrioritySelect from '../components/TargetsPanel/PrioritySelect';
import TargetFeedback from '../components/TargetsPanel/TargetFeedback';
import TargetTolerance from '../components/TargetsPanel/TargetTolerance';
import { TargetSectionProps, TargetType } from './TargetSection.types';

const TargetSection: React.FC<TargetSectionProps> = ({
  label,
  target,
  targetType,
  icon,
  iconSize,
  hasBorder,
  currentOption,
  errorMessage,
  isTargetValueRequired = false,
  isDisabled = false,
  isRange = false,
  isProgrammaticDeal = false,
  dataPendoId,
  isAutoFocus = false,
  chipVisibility = {
    available: false,
    inNetwork: false,
    allocated: false,
  },
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { currencyCode, localeCode } = useSelector((state: Store) => state.publisher.configuration);

  useEffect(() => {
    if (isAutoFocus) {
      inputRef.current?.focus();
    }
  }, [isAutoFocus]);

  const { handleDefaultValueChange, handleMinValueChange, handleMaxValueChange } = target;

  const getSubLabel = (value: number, textSymbol?: string): string => {
    const currency = textSymbol ? `${textSymbol} ` : '';
    const formattedValue = formatNumber({ value, localeCode, fallback: 0, digits: 2 });

    return `${formattedValue}${currency}`;
  };

  const isAvailabilityChip =
    chipVisibility.available && (currentOption?.available !== undefined || target.available !== undefined);
  const isInNetworkChip =
    chipVisibility.inNetwork && (currentOption?.inNetwork !== undefined || target?.inNetwork !== undefined);
  const isAllocationChip =
    chipVisibility.allocated && (currentOption?.allocated !== undefined || target.allocated !== undefined);

  const getTargetErrorMessage = (): string => {
    if (errorMessage) return errorMessage;

    if (isTargetValueRequired && !(currentOption?.target ?? target.value)) {
      return 'Target number is required';
    }

    const targetValue = Number(currentOption?.target ?? target.value);
    if (targetValue) {
      const minConstraint = currentOption?.minValue;
      const maxConstraint = currentOption?.maxValue;

      if (
        minConstraint !== undefined &&
        maxConstraint !== undefined &&
        (targetValue < minConstraint || targetValue > maxConstraint)
      ) {
        return `Value must be between ${minConstraint} and ${maxConstraint}`;
      }

      if (minConstraint !== undefined && targetValue < minConstraint) {
        return `Value must be at least ${minConstraint}`;
      }

      if (maxConstraint !== undefined && targetValue > maxConstraint) {
        return `Value must be at most ${maxConstraint}`;
      }
    }

    return '';
  };

  const defaultValue = currentOption?.defaultValue ?? target.defaultValue;
  const minValue = currentOption?.minValue ?? target.minValue;
  const maxValue = currentOption?.maxValue ?? target.maxValue;

  const rangeValueError = isRangeValueError(minValue, maxValue)
    ? 'Minimum value must be less than or equal to maximum value'
    : '';

  const isBudget = targetType === TargetType.BUDGET;
  const maximumFractionDigits = isBudget ? 2 : 0;

  return (
    <div
      className={cx('p-2', {
        'border border-neutral-200 rounded-lg': hasBorder,
      })}
      data-test-id={`targets-panel-${targetType}-section`}
    >
      <div
        className={cx({
          'w-full flex ': !isProgrammaticDeal,
          'flex-col gap-3': isRange,
          'space-x-2': !isRange,
        })}
      >
        {isRange ? (
          <div className="flex flex-col gap-3">
            {!!handleDefaultValueChange && (
              <InputNumber
                value={defaultValue}
                onChange={(value: number) => handleDefaultValueChange(value, currentOption ?? undefined)}
                id={targetType}
                dataTestId={`target-section-range-default-${targetType}`}
                label="Default"
                isEmpty
                align={InputAlign.RIGHT}
                icon={icon}
                iconSize={iconSize}
                textSymbol={isBudget ? currencyCode : undefined}
                errorMessage={
                  isDefaultValueError(defaultValue, minValue, maxValue)
                    ? 'Default value must be greater or equal to minimum value and less than or equal to maximum value'
                    : ''
                }
                maximumFractionDigits={maximumFractionDigits}
              />
            )}
            {!!handleMinValueChange && (
              <InputNumber
                value={minValue}
                onChange={(value: number) => handleMinValueChange(value, currentOption ?? undefined)}
                id={targetType}
                dataTestId={`target-section-range-min-${targetType}`}
                label="Minimum"
                isEmpty
                align={InputAlign.RIGHT}
                icon={icon}
                iconSize={iconSize}
                textSymbol={isBudget ? currencyCode : undefined}
                errorMessage={rangeValueError}
                maximumFractionDigits={maximumFractionDigits}
              />
            )}
            {!!handleMaxValueChange && (
              <InputNumber
                value={maxValue}
                onChange={(value: number) => handleMaxValueChange(value, currentOption ?? undefined)}
                id={targetType}
                dataTestId={`target-section-range-max-${targetType}`}
                label="Maximum"
                isEmpty
                align={InputAlign.RIGHT}
                icon={icon}
                iconSize={iconSize}
                textSymbol={isBudget ? currencyCode : undefined}
                errorMessage={rangeValueError}
                maximumFractionDigits={maximumFractionDigits}
              />
            )}
          </div>
        ) : (
          <div className={cx({ 'flex-1': !isProgrammaticDeal })}>
            <InputNumber
              inputRef={inputRef}
              value={currentOption?.target ?? target.value}
              onChange={(value: number) => target.handleValueChange(value, currentOption ?? undefined)}
              id={targetType}
              dataTestId={`target-section-${targetType}`}
              label={label}
              icon={icon}
              iconSize={iconSize}
              textSymbol={targetType === TargetType.BUDGET ? currencyCode : undefined}
              isEmpty
              align={InputAlign.RIGHT}
              isDisabled={isDisabled}
              errorMessage={getTargetErrorMessage()}
              data-pendo-id={dataPendoId}
              maximumFractionDigits={maximumFractionDigits}
            />
          </div>
        )}
        <div
          className={cx({
            'flex justify-end mt-3 items-center': isProgrammaticDeal,
            'w-1/4': !isProgrammaticDeal,
            'order-first': isRange,
            'ml-2': !isRange,
          })}
        >
          {!isProgrammaticDeal && (
            <PrioritySelect
              name={`${targetType}-priority`}
              selectedValue={currentOption?.priority ?? target.priority}
              onChange={(value) => target.handlePriorityChange(value, currentOption ?? undefined)}
              isDisabled={isDisabled}
            />
          )}

          {isProgrammaticDeal && (
            <TargetFeedback
              dataTestId={`${targetType}-target-feedback`}
              allocated={currentOption?.allocated ?? target.allocated}
              available={currentOption?.available ?? target.available}
              textSymbol={isBudget ? getCurrency(localeCode, currencyCode) : undefined}
              feedbackColor={getChipColor(currentOption?.status ?? target.feedback)}
              icon={icon}
              isDisabled={isDisabled}
            />
          )}
        </div>
      </div>
      {!isProgrammaticDeal && (
        <>
          <div className="flex justify-end gap-2 my-2 items-center flex-wrap">
            {isAvailabilityChip ? (
              <Chip
                dataTestId="target-section-available"
                color={Color.PRIMARY}
                icon={icon}
                size={Size.SMALL}
                label="Available"
                subLabel={getSubLabel(currentOption?.available || target.available || 0)}
                isDisabled={isDisabled}
              />
            ) : null}
            {isInNetworkChip ? (
              <Chip
                dataTestId="target-section-in-network"
                color={Color.GREY}
                icon={icon}
                size={Size.SMALL}
                label="In Network"
                subLabel={getSubLabel(
                  currentOption?.inNetwork || target.inNetwork || 0,
                  isBudget ? getCurrency(localeCode, currencyCode) : undefined,
                )}
                isDisabled={isDisabled}
              />
            ) : null}
            {isAllocationChip ? (
              <Chip
                dataTestId="target-section-allocated"
                color={getChipColor(currentOption?.status ?? target.feedback)}
                icon={icon}
                size={Size.SMALL}
                label="Allocated"
                subLabel={getSubLabel(
                  currentOption?.allocated || target.allocated || 0,
                  isBudget ? getCurrency(localeCode, currencyCode) : undefined,
                )}
                isDisabled={isDisabled}
              />
            ) : null}
          </div>
          <TargetTolerance
            dataTestId={`${targetType}-tolerance`}
            initialGreen={currentOption?.greenTolerance ?? target.greenTolerance}
            initialOrange={currentOption?.orangeTolerance ?? target.orangeTolerance}
            updateGreenTolerance={(value) => target.handleGreenToleranceChange(value, currentOption ?? undefined)}
            updateOrangeTolerance={(value) => target.handleOrangeToleranceChange(value, currentOption ?? undefined)}
            isDisabled={isDisabled}
          />
        </>
      )}
    </div>
  );
};

export default TargetSection;
